import _ from "lodash";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useANAInfoTracked } from "presentation/store/ANAInfo";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FieldType, HPHInputMask, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionCross, Sidebarheader, SidebarTitle } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";
import AdditionalChargeTablePanel from "./AdditionalChargeTablePanel";
import AdditionalChargeVesselComp from "./Vessel/AdditionalChargeVesselComp";
import NbisDateTimePickerComponent from "presentation/view/components/NbisDateTimePickerComponent";
import { getDefaultBuConfig } from "domain/repository/apiUtil";

const AdditionalChargePanel = () => {
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { additionalState, isShowFindVesselPanel } = counterCustomerChargeState;
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.Detail;
    const messageBarVM = useMessageBarVM();
    const [anaInfoState] = useANAInfoTracked();
    const { operatingUnit } = anaInfoState;
    const blNoMaxLength: number = getDefaultBuConfig<number>("REACT_APP_BILL_OF_LADING_MAX_LENGTH") || 35;

    useEffect(() => {
        if (counterCustomerChargeState.isShowAdditionalChargePanel) {
            const initialScreen = async () => {
                setIsLoading(true);
                try {
                    const ids = counterCustomerChargeState.selectedRows?.filter(row => row.additionalChargeInd === 'Y')?.map(x => x.id);
                    await Promise.allSettled([
                        counterCustomerChargeVM.onSearchAdditional(ids),
                    ]);
                } catch (error) {
                    setIsLoading(false);
                }
            }
            initialScreen().then(() => {
                setIsLoading(false)
            }).catch(error => {
                setIsLoading(false)
            });
        }
    }, [counterCustomerChargeVM, counterCustomerChargeState.selectedRows, counterCustomerChargeState.isShowAdditionalChargePanel])

    const handleClose = useCallback(() => {
        counterCustomerChargeVM.onAdditionalClose();
        counterCustomerChargeVM.onSearchCounterCustomer(counterCustomerChargeState.searchCriteria);
    }, [counterCustomerChargeVM, counterCustomerChargeState.searchCriteria]);

    const handleCoVslVoy = useCallback(() => {
        if (_.isEmpty(additionalState.editingEntity.chargeOnCompany) && _.isEmpty(additionalState.editingEntity.billToCompany) &&
            _.isEmpty(additionalState.editingEntity.consortiumCode) && _.isEmpty(additionalState.editingEntity.vesselCode) &&
            _.isEmpty(additionalState.editingEntity.voyageCode) && _.isEmpty(additionalState.editingEntity.arrDateTime) &&
            _.isEmpty(additionalState.editingEntity.vesselName)) {
            messageBarVM.showError("Please input Charge-On Company, Bill-To Company, Consortium Code, Vessel Code, Voyage Code, Vessel Name at least.");
            return;
        }
        setIsLoading(true);
        counterCustomerChargeVM.onShowPanel(additionalState.editingEntity);
        setIsLoading(false);
    }, [counterCustomerChargeVM, messageBarVM, additionalState.editingEntity]);

    const memoBillOfLading = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={additionalState.allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={additionalState.editingEntity?.billOfLading || ''}
                fieldValue={additionalState.editingEntity?.billOfLading}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_OF_LADING_NO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'billOfLading'}
                maxLength={blNoMaxLength}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [additionalState.allFormState, additionalState.editingEntity?.billOfLading, COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_OF_LADING_NO, blNoMaxLength, counterCustomerChargeVM])

    const memoShippingBill = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={additionalState.allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={additionalState.editingEntity?.shippingBill || ''}
                fieldValue={additionalState.editingEntity?.shippingBill}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPING_BILL}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'shippingBill'}
                maxLength={35}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [additionalState.allFormState, additionalState.editingEntity?.shippingBill, COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPING_BILL, counterCustomerChargeVM])

    const memoOpsLine = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.owner1 || ''}
                fieldValue={additionalState.editingEntity?.owner1}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_LINE}
                isSaveClicked={additionalState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'owner1'}
                maxLength={60}
                options={counterCustomerChargeState.dynamicOptions.opsLineDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_LINE, additionalState.editingEntity?.owner1, additionalState.isSaveClicked, counterCustomerChargeState.dynamicOptions.opsLineDropdownOptions, counterCustomerChargeVM])

    const memoOpsDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <DatePickerComponent
                disabled={false}
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_DATE}
                width="220px"
                date={additionalState.editingEntity?.opsDate}
                fieldName="opsDate"
                errorMessage={additionalState.allFormState ? additionalState.allFormState["opsDate"] : ""}
                onDateChange={counterCustomerChargeVM.onAdditionalFieldChange} />
        </div>
        , [additionalState.editingEntity?.opsDate, additionalState.allFormState, COUNTER_CUSTOMER_CHARGE_CONSTANT.OPS_DATE, counterCustomerChargeVM.onAdditionalFieldChange])

    const memoService = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.serviceCode || ''}
                fieldValue={additionalState.editingEntity?.serviceCode}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SERVICE}
                isSaveClicked={additionalState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'serviceCode'}
                maxLength={60}
                options={counterCustomerChargeState.dynamicOptions.serviceCodeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.SERVICE, additionalState.editingEntity?.serviceCode, additionalState.isSaveClicked, counterCustomerChargeState.dynamicOptions.serviceCodeDropdownOptions, counterCustomerChargeVM])

    const memoCoVslVoyVesselName = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "485px", marginBottom: "24px", marginLeft: "15px" }}>
            <HPHInputMask
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.CONS_VSL_VOY_VESSEL_NAME}
                width={'485px'}
                disabled={false}
                value={{
                    co: additionalState.editingEntity.consortiumCode ?? '',
                    vsl: additionalState.editingEntity.vesselCode ?? '',
                    voy: additionalState.editingEntity.voyageCode ?? '',
                    vel: additionalState.editingEntity.vesselName ?? ''
                }}
                fieldConfiguration={{
                    co: { maxLength: 4, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vsl: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    voy: { maxLength: 10, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                    vel: { maxLength: 30, autoComplete: "off", valueInNormalCase: true, isNumeric: false },
                }}
                onDataChange={(e: any) => counterCustomerChargeVM.onCoVslVoyVelChange(e, {
                    co: 'consortiumCode',
                    vsl: 'vesselCode',
                    voy: 'voyageCode',
                    vel: 'vesselName'
                })} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.CONS_VSL_VOY_VESSEL_NAME, additionalState.editingEntity.consortiumCode, additionalState.editingEntity.vesselCode, additionalState.editingEntity.voyageCode, additionalState.editingEntity.vesselName, counterCustomerChargeVM])

    const memoArrDate = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisDateTimePickerComponent
                disabled={true}
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.ARR_DATE}
                width="220px"
                date={additionalState.editingEntity?.arrDateTime}
                fieldName="arrDateTime"
                errorMessage={""}
                onDateChange={counterCustomerChargeVM.onAdditionalFieldChange} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.ARR_DATE, additionalState.editingEntity?.arrDateTime, counterCustomerChargeVM.onAdditionalFieldChange])

    const memoEtd = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisDateTimePickerComponent
                disabled={true}
                label={COUNTER_CUSTOMER_CHARGE_CONSTANT.ETD}
                width="220px"
                date={additionalState.editingEntity?.etd}
                fieldName="etd"
                errorMessage={""}
                onDateChange={counterCustomerChargeVM.onAdditionalFieldChange} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.ETD, additionalState.editingEntity?.etd, counterCustomerChargeVM.onAdditionalFieldChange])

    const memoAgentNo = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.agentNo || ''}
                fieldValue={additionalState.editingEntity?.agentNo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_NO}
                isSaveClicked={additionalState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'agentNo'}
                maxLength={60}
                options={counterCustomerChargeState.dynamicOptions.agentNoDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_NO, additionalState.editingEntity?.agentNo, additionalState.isSaveClicked, counterCustomerChargeState.dynamicOptions.agentNoDropdownOptions, counterCustomerChargeVM])

    const memoChargeOnCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={additionalState.allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={additionalState.editingEntity?.chargeOnCompany || ''}
                fieldValue={additionalState.editingEntity?.chargeOnCompany}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.CHARGE_ON_COMPANY}
                isSaveClicked={additionalState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'chargeOnCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={counterCustomerChargeState.dynamicOptions.allCCChgOnCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [additionalState.allFormState, additionalState.editingEntity?.chargeOnCompany, additionalState.isSaveClicked, COUNTER_CUSTOMER_CHARGE_CONSTANT.CHARGE_ON_COMPANY, counterCustomerChargeState.dynamicOptions.allCCChgOnCompDropdownOptions, counterCustomerChargeVM])

    const memoBillToCompany = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={additionalState.allFormState}
                isReadOnly={false}
                isShowOptional={false}
                readOnlyValue={additionalState.editingEntity?.billToCompany || ''}
                fieldValue={additionalState.editingEntity?.billToCompany}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_TO_COMPANY}
                isSaveClicked={additionalState.isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'billToCompany'}
                maxLength={60}
                isLimitOptions={true}
                options={counterCustomerChargeState.dynamicOptions.allCCBillToCompDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [additionalState.allFormState, additionalState.editingEntity?.billToCompany, additionalState.isSaveClicked, COUNTER_CUSTOMER_CHARGE_CONSTANT.BILL_TO_COMPANY, counterCustomerChargeState.dynamicOptions.allCCBillToCompDropdownOptions, counterCustomerChargeVM])

    const memoShipperConsignee = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.counterCustCompCode ?? ''}
                fieldValue={additionalState.editingEntity?.counterCustCompCode}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPER_CONSIGNEE}
                isSaveClicked={false}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'counterCustCompCode'}
                maxLength={60}
                options={counterCustomerChargeState.dynamicOptions.shipperConsigneeDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [additionalState.editingEntity?.counterCustCompCode, COUNTER_CUSTOMER_CHARGE_CONSTANT.SHIPPER_CONSIGNEE, counterCustomerChargeState.dynamicOptions.shipperConsigneeDropdownOptions, counterCustomerChargeVM])

    const memoPOO = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.poo || ''}
                fieldValue={additionalState.editingEntity?.poo}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.POO}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'poo'}
                maxLength={5}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.POO, additionalState.editingEntity?.poo, counterCustomerChargeVM])

    const memoPOD = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.pod1 || ''}
                fieldValue={additionalState.editingEntity?.pod1}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.POD}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'pod1'}
                maxLength={5}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.POD, additionalState.editingEntity?.pod1, counterCustomerChargeVM])

    const memoPOL = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.pol || ''}
                fieldValue={additionalState.editingEntity?.pol}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.POL}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'pol'}
                maxLength={5}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.POL, additionalState.editingEntity?.pol, counterCustomerChargeVM])


    const memoFD = useMemo(() =>
        <div className='im-flex-row-item' style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
            <NbisCommonField
                errorMessages={{}}
                isReadOnly={false}
                isShowOptional={true}
                readOnlyValue={additionalState.editingEntity?.fd || ''}
                fieldValue={additionalState.editingEntity?.fd}
                fieldLabel={COUNTER_CUSTOMER_CHARGE_CONSTANT.FD}
                isSaveClicked={false}
                fieldType={FieldType.TEXT}
                fieldKey={'fd'}
                maxLength={5}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    counterCustomerChargeVM.onAdditionalFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [COUNTER_CUSTOMER_CHARGE_CONSTANT.FD, additionalState.editingEntity?.fd, counterCustomerChargeVM])

    return <>
        <div className={`main-comp-wrapper`}>
            <TableWrapper>
                {(isLoading) && <Loader Indicator="Spinner" size="Medium" />}
                <div className={'flex-row'}>
                    <div className={'flex-row-item flex-row-item-full-width'}>
                        <Sidebarheader>
                            <SidebarTitle></SidebarTitle>
                            <SidebarActionCross>
                                <IconButton fileName="Icon-cross" size="medium" disabled={false} toolTipArrow={false} toolTipPlacement="left" toolTipText={'Close'} onClick={handleClose} />
                            </SidebarActionCross>
                        </Sidebarheader>
                    </div>
                </div>
                <Sidebarheader style={{ width: '100%' }}>
                    <CriteriaItemContainer>
                        {memoBillOfLading}
                        {memoShippingBill}
                        {memoOpsLine}
                        {memoOpsDate}
                        {memoService}
                        {memoArrDate}
                        {memoEtd}
                        {memoAgentNo}
                        {memoChargeOnCompany}
                        {memoBillToCompany}
                        {memoShipperConsignee}
                        {(operatingUnit === 'OICT' || operatingUnit === 'HPJZ') && memoPOO}
                        {(operatingUnit === 'OICT' || operatingUnit === 'HPJZ') && memoPOD}
                        {(operatingUnit === 'OICT' || operatingUnit === 'HPJZ') && memoPOL}
                        {(operatingUnit === 'OICT' || operatingUnit === 'HPJZ') && memoFD}
                        {memoCoVslVoyVesselName}
                        <div style={{ marginTop: "15px", marginLeft: "-30px" }}>
                            <IconButton fileName="Icon-more" tooltipDisable={true} size="medium" disabled={false} onClick={handleCoVslVoy} />
                        </div>
                    </CriteriaItemContainer>
                </Sidebarheader>
                {<AdditionalChargeTablePanel />}
                {isShowFindVesselPanel && <AdditionalChargeVesselComp />}
                {additionalState.forceRefresh && <></>}
            </TableWrapper>
        </div>
    </>
}

export default memo(AdditionalChargePanel);